import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, CheckCircle2, Download, Lock, Loader2, ArrowLeft, History, Trash2, Zap } from "lucide-react";
import { api, API } from "@/lib/api";

const PHASE_TITLES = [
  "Matriz de Evidencias",
  "Auditoría forense por disciplinas",
  "Benchmark competitivo",
  "Auditoría E-E-A-T",
  "Auditoría GEO",
  "Puntuación global",
  "Matriz Riesgo/Beneficio",
  "Backlog técnico ejecutable",
];

export default function AuditDetail() {
  const { id } = useParams();
  const [audit, setAudit] = useState(null);
  const [selected, setSelected] = useState(1);
  const [running, setRunning] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const abortRef = useRef(null);

  const load = useCallback(() => api.get(`/audits/${id}`).then((r) => setAudit(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  if (!audit) return <div className="p-10">Cargando…</div>;

  const phaseEntry = (audit.phases || []).find((p) => p.id === selected);
  const canRun = selected <= audit.current_phase;

  const runStream = async () => {
    setRunning(true);
    setStreamText("");
    const url = `${API}/audits/${id}/phases/${selected}/stream`;
    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const resp = await fetch(url, { signal: controller.signal, credentials: "include" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const raw of events) {
          const line = raw.replace(/^data:\s?/, "").trim();
          if (!line) continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === "delta") setStreamText((s) => s + ev.content);
            else if (ev.type === "done") { setAudit(ev.audit); toast.success(`Fase ${selected} generada`); }
            else if (ev.type === "error") toast.error(ev.message || "Error en streaming");
          } catch (parseErr) {
            console.warn("SSE parse error, ignoring line:", parseErr?.message, line?.slice(0, 80));
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Error al ejecutar fase");
    } finally { setRunning(false); abortRef.current = null; }
  };

  const stopStream = () => { if (abortRef.current) abortRef.current.abort(); };

  const approve = async () => {
    if (!window.confirm(`APROBAR FASE ${selected}. ¿Confirmas que has revisado el resultado y quieres avanzar?`)) return;
    try {
      const r = await api.post(`/audits/${id}/phases/${selected}/approve`);
      setAudit(r.data);
      toast.success(`Fase ${selected} aprobada`);
      if (selected < 8) setSelected(selected + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const openHistory = async () => {
    const r = await api.get(`/audits/${id}/phases/${selected}/history`);
    setHistory(r.data.history || []);
    setShowHistory(true);
  };

  const restore = async (idx) => {
    if (!window.confirm("¿Restaurar esta versión? La actual se guardará en el historial.")) return;
    try {
      const r = await api.post(`/audits/${id}/phases/${selected}/restore/${idx}`);
      setAudit(r.data); setShowHistory(false);
      toast.success("Versión restaurada");
    } catch (e) { toast.error("No se pudo restaurar"); }
  };

  const exportMd = async () => {
    const r = await api.get(`/audits/${id}/export`);
    const blob = new Blob([r.data.markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `auditoria-${audit.site_name.replace(/\s+/g,"-").toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="audit-detail-page" className="p-8 lg:p-10">
      <Link to="/audit" data-testid="audit-back" className="inline-flex items-center gap-1 text-sm text-[hsl(var(--audit-muted))] hover:text-[hsl(var(--audit-text))]">
        <ArrowLeft size={14} /> Auditorías
      </Link>
      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{audit.site_name}</h1>
          <div className="mt-1 font-mono text-xs text-[hsl(var(--audit-muted))]">{audit.target_url}</div>
          <p className="mt-3 max-w-2xl text-sm text-[hsl(var(--audit-muted))]">{audit.context}</p>
        </div>
        <button onClick={exportMd} data-testid="audit-export-btn" className="inline-flex items-center gap-2 rounded-md border border-[hsl(var(--audit-border))] bg-white px-4 py-2 text-sm font-semibold hover:bg-[hsl(var(--audit-bg))]">
          <Download size={16} /> Exportar Markdown
        </button>
      </div>

      <div className="mt-8 grid grid-cols-4 gap-0 border border-[hsl(var(--audit-border))] bg-white lg:grid-cols-8">
        {PHASE_TITLES.map((title, idx) => {
          const num = idx + 1;
          const entry = (audit.phases || []).find((p) => p.id === num);
          const state = entry?.status;
          const locked = num > audit.current_phase;
          const active = selected === num;
          return (
            <button
              key={num}
              onClick={() => setSelected(num)}
              data-testid={`phase-tab-${num}`}
              className={`flex flex-col items-start border-r border-b border-[hsl(var(--audit-border))] p-3 text-left last:border-r-0 lg:border-b-0 ${active ? "bg-[hsl(var(--audit-bg))]" : "hover:bg-[hsl(var(--audit-bg))]"}`}
            >
              <div className="flex w-full items-center gap-1 font-mono text-xs text-[hsl(var(--audit-muted))]">
                {String(num).padStart(2, "0")}
                {locked && <Lock size={10} />}
                {state === "generated" && <span className="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">GEN</span>}
                {state === "approved" && <CheckCircle2 size={12} className="ml-auto text-emerald-600" />}
              </div>
              <div className="mt-1 text-xs font-semibold">{title}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 rounded-md border border-[hsl(var(--audit-border))] bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-bold">Fase {selected} · {PHASE_TITLES[selected - 1]}</h2>
          <div className="flex flex-wrap gap-2">
            {phaseEntry?.history?.length > 0 && (
              <button onClick={openHistory} data-testid="phase-history-btn" className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--audit-border))] px-3 py-1.5 text-xs font-semibold">
                <History size={12} /> Historial ({phaseEntry.history.length})
              </button>
            )}
            {!phaseEntry && canRun && (
              <button onClick={runStream} disabled={running} data-testid="run-phase-btn" className="inline-flex items-center gap-2 rounded-md bg-[hsl(var(--audit-text))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {running ? <><Loader2 size={14} className="animate-spin" /> Ejecutando…</> : <><Zap size={14} /> Ejecutar (streaming)</>}
              </button>
            )}
            {phaseEntry && phaseEntry.status !== "approved" && (
              <>
                <button onClick={runStream} disabled={running} data-testid="regen-phase-btn" className="inline-flex items-center gap-2 rounded-md border border-[hsl(var(--audit-border))] px-4 py-2 text-sm font-semibold disabled:opacity-50">
                  {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Regenerar
                </button>
                <button onClick={approve} data-testid="approve-phase-btn" className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">
                  <CheckCircle2 size={14} /> APROBAR FASE {selected}
                </button>
              </>
            )}
            {phaseEntry?.status === "approved" && (
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700">
                <CheckCircle2 size={14} /> Aprobada
              </span>
            )}
            {running && (
              <button onClick={stopStream} className="inline-flex items-center gap-1 rounded-md border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700">
                Detener
              </button>
            )}
          </div>
        </div>

        {!canRun && !phaseEntry && (
          <p className="mt-6 rounded-md bg-[hsl(var(--audit-bg))] p-4 text-sm text-[hsl(var(--audit-muted))]">
            Aprueba las fases anteriores antes de continuar.
          </p>
        )}

        {running && streamText && (
          <div className="mt-6" data-testid="stream-output">
            <div className="mb-2 font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">Recibiendo respuesta…</div>
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-md bg-[hsl(var(--audit-bg))] p-4 font-mono text-xs">{streamText}<span className="animate-pulse">▊</span></pre>
          </div>
        )}

        {phaseEntry && !running && (
          <div className="mt-6">
            {phaseEntry.result?.summary && (
              <div className="mb-6 rounded-md border-l-4 border-[hsl(var(--audit-text))] bg-[hsl(var(--audit-bg))] p-4">
                <div className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">Resumen</div>
                <p className="mt-1 text-sm leading-relaxed">{phaseEntry.result.summary}</p>
                <div className="mt-2 text-[10px] font-mono text-[hsl(var(--audit-muted))]">
                  Generado: {new Date(phaseEntry.generated_at).toLocaleString("es-CO")}
                </div>
              </div>
            )}
            <FindingsView phaseNum={selected} findings={phaseEntry.result?.findings || []} />
            {phaseEntry.result?.raw && (!phaseEntry.result?.findings || phaseEntry.result.findings.length === 0) && (
              <pre className="mt-4 max-h-96 overflow-auto rounded-md bg-[hsl(var(--audit-bg))] p-4 font-mono text-xs">{phaseEntry.result.raw}</pre>
            )}
          </div>
        )}
      </div>

      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowHistory(false)}>
          <div className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-md bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Historial · Fase {selected}</h3>
              <button onClick={() => setShowHistory(false)} className="text-[hsl(var(--audit-muted))]">×</button>
            </div>
            <div className="mt-4 space-y-3">
              {history.length === 0 && <p className="text-sm text-[hsl(var(--audit-muted))]">Sin versiones previas.</p>}
              {history.map((h, i) => (
                <div key={h.generated_at || `v-${i}`} className="rounded-md border border-[hsl(var(--audit-border))] p-4" data-testid={`history-version-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-mono text-[hsl(var(--audit-muted))]">v{i + 1} · {new Date(h.generated_at).toLocaleString("es-CO")}</div>
                    <button onClick={() => restore(i)} data-testid={`restore-${i}`} className="rounded-md bg-[hsl(var(--audit-text))] px-3 py-1 text-xs font-semibold text-white">Restaurar</button>
                  </div>
                  <p className="mt-2 text-sm">{h.result?.summary}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function priorityChip(p) {
  const v = String(p || "").toUpperCase();
  if (v.includes("P0") || v === "ALTA" || v === "CRÍTICA") return "bg-red-100 text-red-800 border-red-300";
  if (v.includes("P1") || v === "MEDIA" || v === "ALTO") return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-indigo-100 text-indigo-800 border-indigo-300";
}

function FindingsView({ phaseNum, findings }) {
  if (!findings || findings.length === 0) return null;
  if (phaseNum === 8) {
    return (
      <div className="overflow-x-auto rounded-md border border-[hsl(var(--audit-border))]">
        <table className="w-full font-mono text-xs">
          <thead className="bg-[hsl(var(--audit-bg))] uppercase tracking-widest text-[hsl(var(--audit-muted))]">
            <tr>
              <th className="px-3 py-2 text-left">ID</th><th className="px-3 py-2 text-left">Prioridad</th>
              <th className="px-3 py-2 text-left">Título</th><th className="px-3 py-2 text-left">Archivo</th>
              <th className="px-3 py-2 text-left">Acción</th><th className="px-3 py-2 text-left">Tiempo</th>
              <th className="px-3 py-2 text-left">Validación</th>
            </tr>
          </thead>
          <tbody>
            {findings.map((f, i) => (
              <tr key={f.id || `TASK-${i}`} className="border-t border-[hsl(var(--audit-border))]" data-testid={`backlog-row-${i}`}>
                <td className="px-3 py-2 font-semibold">{f.id || `TASK-${String(i+1).padStart(3,"0")}`}</td>
                <td className="px-3 py-2"><span className={`rounded border px-2 py-0.5 ${priorityChip(f.prioridad)}`}>{f.prioridad || "P2"}</span></td>
                <td className="px-3 py-2">{f.titulo || f.title || ""}</td>
                <td className="px-3 py-2 text-[hsl(var(--audit-muted))]">{f.archivo || "—"}</td>
                <td className="px-3 py-2">{f.accion || f.action || ""}</td>
                <td className="px-3 py-2 text-[hsl(var(--audit-muted))]">{f.tiempo_estimado || f.tiempo || "—"}</td>
                <td className="px-3 py-2 text-[hsl(var(--audit-muted))]">{f.validacion || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      {findings.map((f, i) => (
        <div key={f.id || `finding-${phaseNum}-${i}`} className="rounded-md border border-[hsl(var(--audit-border))] p-4" data-testid={`finding-${phaseNum}-${i}`}>
          <div className="grid gap-2 sm:grid-cols-2">
            {Object.entries(f).map(([k, v]) => (
              <div key={k}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--audit-muted))]">{k}</div>
                <div className="mt-0.5 text-sm">
                  {Array.isArray(v) ? (
                    <ul className="list-inside list-disc space-y-0.5">{v.map((it, j) => <li key={`${k}-${j}`}>{String(it)}</li>)}</ul>
                  ) : (String(v))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
